import type { InputHTMLAttributes, ReactNode } from 'react'
import { forwardRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { HelpCircleIcon } from '@untitledui-icons/react/line'

interface OnboardingInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Label text for the input */
  label?: string
  /** Error message to display */
  error?: string
  /** Hint text shown when clicking the help icon */
  hint?: string
  /** Show required asterisk */
  required?: boolean
  /** Icon to show at the start of input */
  iconLeading?: ReactNode
  /** Show help icon at the end (toggles hint visibility on click) */
  showHelpIcon?: boolean
  /** Help icon click handler (overrides default toggle behavior) */
  onHelpClick?: () => void
  /** Additional content after the input (before error) */
  children?: ReactNode
}

export const OnboardingInput = forwardRef<HTMLInputElement, OnboardingInputProps>(
  ({ 
    label, 
    error, 
    hint, 
    required,
    iconLeading,
    showHelpIcon,
    onHelpClick,
    className, 
    id, 
    children, 
    ...props 
  }, ref) => {
    const inputId = id || props.name
    const [showHint, setShowHint] = useState(false)

    const handleHelpClick = () => {
      if (onHelpClick) {
        onHelpClick()
      } else {
        setShowHint((prev) => !prev)
      }
    }

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium leading-5 text-[#181d27]">
            {label}
            {required && <span className="ml-0.5 text-[#6941c6]">*</span>}
          </label>
        )}
        <div className="relative">
          {iconLeading && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
              <div className="h-5 w-5 text-[#535862]">
                {iconLeading}
              </div>
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'block w-full rounded-lg border border-[#d5d7da] bg-white py-2.5 text-base font-medium text-[#181d27] placeholder:text-[#717680] placeholder:font-normal',
              'focus:outline-none focus:border-[#151515] focus:ring-0 focus:shadow-[inset_0_0_0_1px_#151515]',
              error && 'border-[#f04438] focus:border-[#f04438] focus:shadow-[inset_0_0_0_1px_#f04438]',
              iconLeading ? 'pl-[42px]' : 'pl-3.5',
              showHelpIcon ? 'pr-11' : 'pr-3.5',
              className
            )}
            {...props}
          />
          {showHelpIcon && (
            <button
              type="button"
              onClick={handleHelpClick}
              className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-[#717680] hover:text-[#535862]"
            >
              <HelpCircleIcon className="h-5 w-5" />
            </button>
          )}
        </div>
        {children}
        {hint && showHint && !error && (
          <p className="text-sm leading-5 text-[#535862]">{hint}</p>
        )}
        {error && (
          <p className="text-sm leading-5 text-[#f04438]">{error}</p>
        )}
      </div>
    )
  }
)

OnboardingInput.displayName = 'OnboardingInput'
